import { actorNameFromTitle, getMetadataString } from "./helpers";
import type { Notification } from "./types";

export interface NotificationUserMetadata {
  id: string | null;
  name: string;
  avatarUrl: string | null;
}

type MetadataRecord = Record<string, unknown>;

function isRecord(value: unknown): value is MetadataRecord {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeUser(value: unknown): NotificationUserMetadata | null {
  if (!isRecord(value)) return null;
  const name = nonEmptyString(value.name) ?? nonEmptyString(value.label);
  if (!name) return null;

  return {
    id: nonEmptyString(value.id),
    name,
    avatarUrl:
      nonEmptyString(value.avatar_url) ?? nonEmptyString(value.avatarUrl),
  };
}

function metadataOf(notification: Notification): MetadataRecord | null {
  return isRecord(notification.metadata_json) ? notification.metadata_json : null;
}

export function getNotificationActor(
  notification: Notification,
): NotificationUserMetadata | null {
  const metadata = metadataOf(notification);
  const user = normalizeUser(metadata?.user);
  if (user) return user;

  const legacyName =
    getMetadataString(notification.metadata_json, "actor_name") ??
    actorNameFromTitle(notification.title);
  if (!legacyName) return null;

  return {
    id: null,
    name: legacyName,
    avatarUrl: getMetadataString(notification.metadata_json, "actor_avatar_url"),
  };
}

export function getNotificationTargetUsers(
  notification: Notification,
): NotificationUserMetadata[] {
  const targetUser = metadataOf(notification)?.target_user;
  if (Array.isArray(targetUser)) {
    return targetUser.flatMap((user) => {
      const normalized = normalizeUser(user);
      return normalized ? [normalized] : [];
    });
  }

  const normalized = normalizeUser(targetUser);
  return normalized ? [normalized] : [];
}

export function getNotificationActionUrl(notification: Notification): string | null {
  const metadata = metadataOf(notification);
  if (isRecord(metadata?.action)) {
    const actionUrl = nonEmptyString(metadata.action.url);
    if (actionUrl) return actionUrl;
  }
  if (Array.isArray(metadata?.target)) {
    for (const target of metadata.target) {
      if (!isRecord(target)) continue;
      const targetUrl = nonEmptyString(target.url);
      if (targetUrl) return targetUrl;
    }
  } else if (isRecord(metadata?.target)) {
    const targetUrl = nonEmptyString(metadata.target.url);
    if (targetUrl) return targetUrl;
  }
  return notification.target_url ?? null;
}

export function getNotificationPreviewText(
  notification: Notification,
): string | null {
  const preview = metadataOf(notification)?.preview;
  if (isRecord(preview)) {
    const text = nonEmptyString(preview.text);
    if (text) return text;
  }
  return notification.body ?? null;
}

export function getNotificationProjectId(
  notification: Notification,
): string | null {
  const payload = metadataOf(notification)?.payload;
  if (!isRecord(payload)) return null;
  return nonEmptyString(payload.project_id);
}
